import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DemographicRequestData } from '@models/demographic-request.model';
import { KioskBooking, KioskBookingData } from '@models/kiosk-booking.model';
import {
  IdentifyData,
  Kiosk,
  KioskAvailabilityStatus,
  KioskConnectionStatus,
  KioskDetails,
  KioskForm,
  KioskResult,
  MatchResponse,
  WSKioskMessage,
} from '@models/kiosk.model';
import { ErrorResponse } from '@models/responses.model';
import { TranslateService } from '@ngx-translate/core';
import { TimeoutService } from '@services/timeout.service';
import { EMPTY, from, interval, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, first, map, retry, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AlertService } from './alert.service';
import { PackageService } from './package.service';
import { WebSocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class KioskService implements OnDestroy {
  private static readonly storageClass: Storage = localStorage;
  private readonly KIOSK_NAME_LOCALSTORAGE_KEY = 'kiosk_name';
  private readonly IDENTIFIER_LOCALSTORAGE_KEY = 'kiosk_identifier';
  private readonly FRONT_CAMERA_LOCALSTORAGE_KEY = 'kiosk_frontcamera';
  private readonly BACK_CAMERA_LOCALSTORAGE_KEY = 'kiosk_backcamera';
  private readonly FACIAL_RECOGNITION_LOCALSTORAGE_KEY = 'facial_recognition';
  private readonly INVERT_BACK_CAMERA_XAXIS_LOCALSTORAGE_KEY = 'invert_back_camera_xaxis';
  private readonly INVERT_FRONT_CAMERA_XAXIS_LOCALSTORAGE_KEY = 'invert_front_camera_xaxis';
  private _name: string;
  private _identifier: string;
  private _frontCamera: string;
  private _backCamera: string;
  private _facialRecognition: boolean;
  private _invertBackCameraXAxis: boolean;
  private _invertFrontCameraXAxis: boolean;
  private _request: Partial<DemographicRequestData>;
  booking$ = new ReplaySubject<KioskBooking>(1);
  bookings$ = new ReplaySubject<KioskBooking[]>(1);
  pingSource = interval(9000);
  pingSubscription = this.pingSource.subscribe(() => this.pingKiosk());
  reconnectSource = interval(59000);
  reconnectSubscription = this.reconnectSource.subscribe(() => this.openKioskWebSocket());
  patientCellnr = '';
  patientSurname = '';
  imageToSave: string;
  distance = 1;
  videoList: any = [];
  connectionStatus: KioskConnectionStatus = 'OFFLINE';
  availabilityStatus: KioskAvailabilityStatus = 'READY';
  linkVerified = true;
  public videoOptions: MediaTrackConstraints = { facingMode: { ideal: 'user' } };

  uuid: string;
  private readonly disconnected$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly alert: AlertService,
    private readonly websocket: WebSocketService,
    public readonly timeoutService: TimeoutService,
    private readonly router: Router,
    private readonly packageService: PackageService,
    private readonly translateService: TranslateService,
  ) {
    this._name = KioskService.storageClass.getItem(this.KIOSK_NAME_LOCALSTORAGE_KEY);
    this._identifier = KioskService.storageClass.getItem(this.IDENTIFIER_LOCALSTORAGE_KEY);
    this._frontCamera = KioskService.storageClass.getItem(this.FRONT_CAMERA_LOCALSTORAGE_KEY);
    this._backCamera = KioskService.storageClass.getItem(this.BACK_CAMERA_LOCALSTORAGE_KEY);
    this._facialRecognition = KioskService.storageClass.getItem(this.FACIAL_RECOGNITION_LOCALSTORAGE_KEY) === 'true';
    this._invertBackCameraXAxis = KioskService.storageClass.getItem(this.INVERT_BACK_CAMERA_XAXIS_LOCALSTORAGE_KEY) === 'true';
    this._invertFrontCameraXAxis = KioskService.storageClass.getItem(this.INVERT_FRONT_CAMERA_XAXIS_LOCALSTORAGE_KEY) === 'true';
    this.getWebcamInfo();
    window.navigator.mediaDevices?.addEventListener('devicechange', () => {
      this.getWebcamInfo();
    });

    this.timeoutService.initTimeoutService();
  }

  /* Kiosk flow:
    Get kiosk Name & UUID
    Register Kiosk
    Receive identifier
    ?Can update Kiosk Name
  */

  public set name(newName: string) {
    this._name = newName;
    KioskService.storageClass.setItem(this.KIOSK_NAME_LOCALSTORAGE_KEY, newName);
  }

  public get name() {
    return this._name;
  }

  public get kioskIdentifier() {
    return this._identifier;
  }

  public set frontCamera(newFrontCamera: string) {
    this._frontCamera = newFrontCamera;
    KioskService.storageClass.setItem(this.FRONT_CAMERA_LOCALSTORAGE_KEY, newFrontCamera);
  }

  public get frontCamera() {
    return this._frontCamera;
  }

  public set backCamera(newBackCamera: string) {
    this._backCamera = newBackCamera;
    KioskService.storageClass.setItem(this.BACK_CAMERA_LOCALSTORAGE_KEY, newBackCamera);
  }

  public get backCamera() {
    return this._backCamera;
  }

  public set facialRecognition(newValue: string | boolean) {
    this._facialRecognition = newValue === 'true' || newValue === true;
    KioskService.storageClass.setItem(this.FACIAL_RECOGNITION_LOCALSTORAGE_KEY, String(newValue));
  }

  public get facialRecognition() {
    return this._facialRecognition;
  }

  public set toggleBackCamera(newValue: string | boolean) {
    this._invertBackCameraXAxis = newValue === 'true' || newValue === true;
    KioskService.storageClass.setItem(this.INVERT_BACK_CAMERA_XAXIS_LOCALSTORAGE_KEY, String(newValue));
  }

  public get toggleBackCamera() {
    return this._invertBackCameraXAxis;
  }

  public set toggleFrontCamera(newValue: string | boolean) {
    this._invertFrontCameraXAxis = newValue === 'true' || newValue === true;
    KioskService.storageClass.setItem(this.INVERT_FRONT_CAMERA_XAXIS_LOCALSTORAGE_KEY, String(newValue));
  }

  public get toggleFrontCameraValue() {
    return this._invertFrontCameraXAxis;
  }

  public set identifier(newIdentifier: string) {
    this._identifier = newIdentifier;
    if (newIdentifier) {
      KioskService.storageClass.setItem(this.IDENTIFIER_LOCALSTORAGE_KEY, newIdentifier);
      this.openKioskWebSocket();
    } else {
      KioskService.storageClass.removeItem(this.IDENTIFIER_LOCALSTORAGE_KEY);
      this.closeKioskWebSocket();
    }
  }

  public get identifier() {
    return this._identifier;
  }

  public set request(requestData: Partial<DemographicRequestData>) {
    if (requestData) {
      let i = -1;
      requestData.dependants_json.forEach((element) => {
        element.dbid = i--;
      });
      requestData.main_member_json.dbid = i;
    }
    this._request = requestData;
  }

  public get request(): Partial<DemographicRequestData> {
    return this._request;
  }

  async updateName() {
    this.videoList = await this.getWebcamInfo();
    const CAMERA_QUESTION = [
      { label: this.translateService.instant('name'), required: true, key: 'name', value: this._name },
      {
        key: 'frontCamera',
        label: this.translateService.instant('front_camera'),
        required: false,
        type: 'dropdown',
        value: this._frontCamera,
        options: this.videoList,
        optionValue: (r) => r.deviceId,
        optionLabel: (r) => r.label,
      },
      {
        key: 'toggleFrontCamera',
        type: 'checkbox',
        value: this._invertFrontCameraXAxis,
        label: this.translateService.instant('flip_front_camera_x_axis'),
      },
      {
        key: 'backCamera',
        label: this.translateService.instant('back_camera'),
        required: false,
        type: 'dropdown',
        value: this._backCamera,
        options: this.videoList,
        optionValue: (r) => r.deviceId,
        optionLabel: (r) => r.label,
      },
      {
        key: 'toggleBackCamera',
        type: 'checkbox',
        value: this._invertBackCameraXAxis,
        label: this.translateService.instant('flip_back_camera_x_axis'),
      },
      {
        key: 'facialRecognition',
        type: 'checkbox',
        value: this._facialRecognition,
        label: this.translateService.instant('enable_facial_recognition'),
      },
    ];
    const config = { backdropClass: 'blur', hasBackdrop: true, disableClose: true };
    const dialog = {
      title: this.translateService.instant('kiosk_settings'),
      tag: 'kiosk',
      submitText: this.translateService.instant('submit'),
      cancelText: this.translateService.instant('cancel'),
      questions: CAMERA_QUESTION,
    };
    this.alert
      .inputDialog$(dialog, config)
      .pipe(
        tap((data) => {
          if (!data.name) {
            throwError({ error: 'kiosk/error', message: 'Invalid name' } as ErrorResponse);
          }
        }),
        retry(1),
      )
      .subscribe((data) => {
        if (data.name) {
          this.name = data.name;
          this.frontCamera = data.frontCamera;
          this.backCamera = data.backCamera;
          this.facialRecognition = data.facialRecognition;
          this.toggleBackCamera = data.toggleBackCamera;
          this.toggleFrontCamera = data.toggleFrontCamera;
          this.register();
        } else if (!this.name) {
          this.updateName();
        }
      });
  }

  async initialize(uuid: string) {
    await this.getUserMedia();
    this.uuid = uuid;
    if (!this.name) {
      this.updateName();
    } else {
      this.register();
    }
  }

  // getUserMedia asks for permission
  async getUserMedia() {
    const mediaDevices = await navigator.mediaDevices.enumerateDevices();
    if (mediaDevices.some((dev) => dev.kind === 'videoinput')) {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
      } catch {
        this.alert.snackbar('camera_permission_denied', 'close', 5000);
      }
    }
  }

  private register() {
    if (this.identifier) {
      this.updateKiosk();
    } else {
      this.createKiosk();
    }
  }

  private createKiosk() {
    const kioskDataPost: Kiosk = {
      name: this._name,
      uuid: this.uuid,
    };
    this.http.post<KioskResult>('/api/kiosk', kioskDataPost).subscribe(
      (response) => {
        this.connectionStatus = 'ONLINE';
        this.availabilityStatus = 'READY';
        this.identifier = response.identifier;
      },
      (err: unknown) => {
        this.connectionStatus = 'OFFLINE';
        this.alert.handleErrorDialog$(err, 'Kiosk Offline');
      },
    );
  }

  private updateKiosk() {
    const kioskDataPatch: Kiosk = {
      name: this._name,
      uuid: this.uuid,
    };
    this.http
      .patch<KioskResult>(`/api/kiosk/${this.identifier}`, kioskDataPatch)
      .pipe(
        catchError((error: unknown) => {
          this.identifier = undefined;
          this.createKiosk();
          if (error instanceof HttpErrorResponse) {
            return throwError(error.statusText);
          }
          return throwError('Kiosk could not be updated. Please reload this page');
        }),
      )
      .subscribe((response) => {
        this.identifier = response.identifier;
        this.connectionStatus = 'ONLINE';
        this.availabilityStatus = 'READY';
      });
  }

  private openKioskWebSocket() {
    this.closeKioskWebSocket();
    this.websocket.connect(`/websocket/kiosk/${this.identifier}`).pipe(takeUntil(this.disconnected$)).subscribe();
    this.websocket
      .listen$('kiosk_data')
      .pipe(
        tap((data: WSKioskMessage) => {
          const clinicalURL = {
            consent_form: 'consentforms',
            questionnaire: 'questionnaires',
            demographic_request: 'demographic-requests',
          }[data.type];
          if (clinicalURL) {
            this.http.get<KioskForm>(`/api/kiosk/${this.identifier}/clinical/${clinicalURL}/${data.uuid}`).subscribe((response) => {
              if (response) {
                // Auto verify on kiosk
                if (response.patient_surname) {
                  this.packageService.identifier = response.patient_surname;
                } else if (response.patient_json && response.patient_json.surname) {
                  this.packageService.identifier = response.patient_json.surname;
                }
                if (response.form_authentication) {
                  this.router.navigate([`/kiosk/${this.uuid}/package/${data.uuid}`]);
                } else {
                  this.router.navigate([
                    `/kiosk/${this.uuid}/package/${data.uuid}/${this.packageService.identifier ? this.packageService.identifier : ''}`,
                  ]);
                }
              }
            });
          } else if (data.type === 'reset') {
            this.router.navigate([`/kiosk/${this.uuid}`]);
          }
        }),
      )
      .subscribe();
  }

  private closeKioskWebSocket() {
    this.disconnected$.next();
    this.websocket.close();
  }

  setKioskStatus(status: KioskAvailabilityStatus) {
    const data = {
      status,
      identifier: this.identifier,
    };
    this.http.post('/api/kioskstatus', data).subscribe((identifier) => {
      if (identifier) {
        this.availabilityStatus = status;
      }
    });
  }

  pingKiosk() {
    if (this.identifier) {
      this.http
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .get(`/api/kioskping/${this.identifier}`, { headers: { 'http-loader-disable': 'disable' } })
        .pipe(
          first(),
          catchError((error: unknown) => {
            this.alert.snackbar('temporary_connection_issue', 'close', 10000);
            this.connectionStatus = 'OFFLINE';
            if (error instanceof HttpErrorResponse) {
              return throwError(error.statusText);
            }
          }),
        )
        .subscribe((kioskDetails: KioskDetails) => {
          if (kioskDetails?.status) {
            this.connectionStatus = 'ONLINE';
            if (kioskDetails.status === 'BUSY') {
              this.availabilityStatus = 'BUSY';
            } else {
              this.availabilityStatus = 'READY';
            }
          } else {
            this.connectionStatus = 'OFFLINE';
          }
          if ([kioskDetails?.diary_uuid, kioskDetails?.practice_uuid].includes(this.uuid)) {
            this.linkVerified = true;
          } else {
            this.linkVerified = false;
          }
        });
    }
  }

  refreshCache() {
    KioskService.storageClass.removeItem(this.IDENTIFIER_LOCALSTORAGE_KEY);
    KioskService.storageClass.removeItem(this.KIOSK_NAME_LOCALSTORAGE_KEY);
    KioskService.storageClass.removeItem(this.FRONT_CAMERA_LOCALSTORAGE_KEY);
    KioskService.storageClass.removeItem(this.BACK_CAMERA_LOCALSTORAGE_KEY);
    KioskService.storageClass.removeItem(this.FACIAL_RECOGNITION_LOCALSTORAGE_KEY);
    KioskService.storageClass.removeItem(this.INVERT_BACK_CAMERA_XAXIS_LOCALSTORAGE_KEY);
    KioskService.storageClass.removeItem(this.INVERT_FRONT_CAMERA_XAXIS_LOCALSTORAGE_KEY);
    window.location.reload();
  }

  getBooking(data: IdentifyData) {
    this.http
      .get<[KioskBookingData]>(`/api/kioskbooking/${this.uuid}/${data.surname}/${data.cellnr}`)
      .pipe(map((items) => items.map((item) => new KioskBooking(item))))
      .subscribe(
        (bookingsData) => bookingsData,
        () => {
          this.alert.snackbar('no_booking_found');
          this.router.navigate(['kiosk', this.uuid, 'reception']);
        },
      );
  }

  getBookings(data: IdentifyData) {
    this.http
      .get<[KioskBookingData]>(`/api/kioskbooking/${this.uuid}/${data.surname}/${data.cellnr}`)
      .pipe(map((items) => items.map((item) => new KioskBooking(item))))
      .subscribe(
        (bookingsData) => {
          this.bookings$.next(bookingsData);
        },
        () => {
          this.alert.snackbar('no_booking_found');
          this.router.navigate(['kiosk', this.uuid, 'reception']);
        },
      );
  }

  setBookingStatus(booking: KioskBooking, status: string, face = '', distance = null) {
    const statusDataPost = {
      diary_uuid: booking.diaryUuid,
      booking_uuid: booking.bookingUuid,
      status,
      face,
      distance,
    };
    return this.http.post('/api/kiosk/booking_status', statusDataPost);
  }

  submitDebtorImport() {
    const demographicRequest = {
      ...this._request,
      kiosk_identifier: this.identifier,
    };
    return this.http.post('/api/kiosk/clinical/demographic-request', demographicRequest);
  }

  cropImageCentered$(base64Image: string, xPercentage: number, yPercentage: number): Observable<string> {
    const promise = new Promise<string>((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        // if image width is 200px and xPercentage is 10
        // We want to take 10% off of each side, aka 20% total
        // 200(1 - (10*2)/100) = 200(1 - 0.2) = 200(0.8) = 160
        const width = img.width * (1 - (2 * xPercentage) / 100);
        const height = img.height * (1 - (2 * yPercentage) / 100);

        canvas.width = width;
        canvas.height = height;
        // Calculate the x and y offsets to center the cropped image
        const xOffset = (img.width - width) / 2;
        const yOffset = (img.height - height) / 2;

        ctx.drawImage(img, xOffset, yOffset, width, height, 0, 0, width, height);
        // Convert the cropped image to a data URL and resolve the Promise with the result
        const croppedImage = canvas.toDataURL();
        resolve(croppedImage);
      };
      img.onerror = reject;
      img.src = base64Image;
    });
    return from(promise);
  }

  facialRecognitionMatch(image: string) {
    this.http
      .post('/api/auth/facial/recognition/match', { image, uuid: this.uuid })
      .pipe(
        take(1),
        catchError((err: unknown) => {
          this.alert.handleErrorDialog$(err, this.translateService.instant('error_finding_booking_for_patient'));
          this.router.navigate(['kiosk', this.uuid, 'identify']);
          return of(err);
        }),
        switchMap((usersDetails: { result: [MatchResponse] }) => {
          if (usersDetails.result.length) {
            this.distance = usersDetails.result[0].distance;
          }
          const size = 175; // The width in px of the image to be saved
          //create image so that we get image width and height
          const x = document.createElement('canvas');
          const ctx = x.getContext('2d');
          const loadedImage = new Image();
          loadedImage.src = image;

          const ratio = loadedImage.width / loadedImage.height;
          const height = size / ratio;
          x.width = size;
          x.height = height;

          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0, size, height);
            this.imageToSave = x.toDataURL('image/jpeg');
          };
          img.src = image;

          if (usersDetails.result.length && this.distance < 0.6) {
            this.patientSurname = usersDetails.result[0].surname;
            this.patientCellnr = usersDetails.result[0].cellnr;
            this.packageService.identifier = this.patientSurname;
            this.getBookings({ surname: usersDetails.result[0].surname, cellnr: this.patientCellnr });
          } else {
            this.router.navigate(['kiosk', this.uuid, 'identify']);
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  async getWebcamInfo() {
    const mediaDevices = await navigator.mediaDevices?.enumerateDevices();
    const videoList = [];
    if (!!mediaDevices?.length) {
      mediaDevices.forEach((dev) => {
        // BCM2835 is the Raspberry Pi's GPU, we don't want to use that
        if (dev.kind === 'videoinput' && dev.deviceId && !dev.label.toLowerCase().includes('bcm2835')) {
          const item = {
            deviceId: dev.deviceId,
            label: dev.label,
            kind: dev.kind,
          };
          videoList.push(item);
        }
      });
    }
    return videoList;
  }

  ngOnDestroy() {
    this.timeoutService.removeTimeOut();
    this.reconnectSubscription.unsubscribe();
    this.closeKioskWebSocket();
    this.pingSubscription.unsubscribe();
  }
}
